import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@els/els-react--loader';
import store from '@Store';
import uuid from 'uuid/v4';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import useStructureSrc from '@Containers/StructureDialog/useStructureSrc';
import ImageBase64 from '@Components/ImageBase64';
import Tooltip, { TooltipConstants } from '@Components/Tooltip';
import HoverTooltip from '@Components/HoverTooltip';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';

import './Substructure.scss';
import { ButtonWithIcon } from '@els/els-react--button';

function Substructure({ smiles, removeSmiles, displayRemoveIcon = true }) {
  const structureSrc = useStructureSrc(smiles.value);

  const zoomSubstructure = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      store.dispatch(
        openStructureDialog(uuid(), smiles.value, {
          name: parseV3000SumFormula(smiles.value),
          isLinearFormula: true,
        })
      );
    },
    [smiles]
  );

  const copySmiles = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      navigator.clipboard?.writeText(smiles.value);
      showCopiedToastMessage('The SMILES string of the structure has been copied to the clipboard.');
    },
    [smiles]
  );

  return (
    <div className="substructure">
      <ImageBase64 src={structureSrc}>
        {({ src }) =>
          src ? (
            <>
              <div className="substructure__smiles-image-wrapper">
                <img src={src} alt={smiles.value} className="substructure__smiles-image" />
              </div>
              <div className="substructure__smiles-icons">
                <HoverTooltip
                  theme="simple"
                  className="substructure__smiles-icon-wrapper"
                  id={TooltipConstants.ids.RetrosynthesisStructureIcon}
                  tooltip="Zoom">
                  <ButtonWithIcon
                    data-e2e="zoom-structure-button"
                    type="link"
                    linkIconSize="xs"
                    sprite="ZoomIn"
                    iconOnly
                    onClick={zoomSubstructure}
                    className="substructure__smiles-icon zoom e2e-copy">
                    Zoom
                  </ButtonWithIcon>
                </HoverTooltip>
                <HoverTooltip
                  theme="simple"
                  className="substructure__smiles-icon-wrapper"
                  id={TooltipConstants.ids.RetrosynthesisStructureIcon}
                  tooltip="Copy SMILES">
                  <ButtonWithIcon
                    data-e2e="copy-smiles-button"
                    type="link"
                    sprite="DuplicateDocument"
                    iconOnly
                    linkIconSize="xs"
                    className="substructure__smiles-icon delete e2e-copy"
                    onClick={copySmiles}>
                    Copy SMILES
                  </ButtonWithIcon>
                </HoverTooltip>
                {displayRemoveIcon && (
                  <HoverTooltip
                    className="substructure__smiles-icon-wrapper"
                    theme="simple"
                    id={TooltipConstants.ids.RetrosynthesisStructureIcon}
                    tooltip="Delete structure">
                    <ButtonWithIcon
                      data-e2e="delete-structure-button"
                      type="link"
                      sprite="DeleteDocument"
                      iconOnly
                      linkIconSize="xs"
                      className="substructure__smiles-icon delete e2e-copy"
                      onClick={() => {
                        removeSmiles(smiles);
                        Tooltip.hide();
                      }}>
                      Delete structure
                    </ButtonWithIcon>
                  </HoverTooltip>
                )}
              </div>
            </>
          ) : (
            <div className="substructure__smiles-image-wrapper">
              <Loader isSmall isBlocking={false} className="substructure__image-loader" />
            </div>
          )
        }
      </ImageBase64>
    </div>
  );
}

Substructure.propTypes = {
  smiles: PropTypes.object.isRequired,
  removeSmiles: PropTypes.func,
  displayRemoveIcon: PropTypes.bool,
};

export default Substructure;
